import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { LDFlags } from '../../../constants/flags';
import { DealType, useUserDealQuery } from '../../../gql/generated/graphql';
import { useFlag } from '../../../hooks';
import { useMarketplaceExternalId } from '../../../services/marketplace/externalId';
import { useGetLeadByExternalId } from '../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { delistModalOpenAtom } from '../RootDashboard';
import {
  DELIST_HANDOFF_WINDOW_MS,
  isLeadOnMarketplacePath,
  isRecentKeepCarDelist,
} from '../components/marketplace/listingStatus';
import { UserDeal } from '../utils';
import useEffectiveDealFields from './useEffectiveDealFields';

const DELIST_HOLD_POLL_MS = 3_000;

type DealPolling = Pick<ReturnType<typeof useUserDealQuery>, 'startPolling' | 'stopPolling'>;

type MarketplaceView = {
  isMarketplace: boolean;
  resolving: boolean;
};

const useMarketplaceView = (
  userDeal: UserDeal,
  { startPolling, stopPolling }: DealPolling,
): MarketplaceView => {
  const enabled = useFlag(LDFlags.MARKETPLACE_DASHBOARD);
  const { type, isHandedBack } = useEffectiveDealFields(userDeal);
  const delistModalOpen = useAtomValue(delistModalOpenAtom);
  const [holdExpired, setHoldExpired] = useState(false);

  const isMarketplaceType = type === DealType.Marketplace;

  const externalId = useMarketplaceExternalId(userDeal?.id ?? undefined);
  const { data: lead, isLoading: leadLoading } = useGetLeadByExternalId(
    { externalId },
    { query: { enabled: enabled && (isMarketplaceType || isHandedBack) && externalId !== '' } },
  );

  const isMarketplace =
    enabled && (isMarketplaceType || (isHandedBack && isLeadOnMarketplacePath(lead)));
  const awaitingHandoff =
    isMarketplace && !delistModalOpen && !holdExpired && isRecentKeepCarDelist(lead);

  useEffect(() => {
    if (!awaitingHandoff) return undefined;
    startPolling(DELIST_HOLD_POLL_MS);
    const cap = setTimeout(() => setHoldExpired(true), DELIST_HANDOFF_WINDOW_MS);
    return () => {
      stopPolling();
      clearTimeout(cap);
    };
  }, [awaitingHandoff, startPolling, stopPolling]);

  return { isMarketplace, resolving: (isHandedBack && leadLoading) || awaitingHandoff };
};

export default useMarketplaceView;
